import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  Receipt,
  User,
  Phone,
  IndianRupee,
  FileText,
  Loader2,
  Sparkles,
  CreditCard,
  Hash,
} from "lucide-react";
import { createManualCustomerPaymentAPI } from "../../../../Redux/ApiCalls/Dashboard/CustomerTransationApi";
import { trackClarityEvent } from "../../../../analytics/ClarityAnalytics";
import { CLARITY_EVENTS } from "../../../../analytics/EventTypes";
import { showSuccessToast, showErrorToast } from "../../Utlis/Toast";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ModalActions,
  SecondaryButton,
  PrimaryButton,
} from "../../../organisms/Dashboard/quotationMasterListShared";
import {
  Field,
  FieldGrid,
  Label,
  Input,
  ErrorSpan,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

const MANUAL_PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "neft", label: "NEFT" },
  { value: "imps", label: "IMPS" },
  { value: "cheque", label: "Cheque" },
  { value: "manual", label: "Manual" },
  { value: "other", label: "Other" },
];

const initialFormData = {
  firstName: "",
  lastName: "",
  contact: "",
  amount: "",
  paymentMethod: "cash",
  transactionReference: "",
  description: "",
};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 14px;
  color: #171717;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }

  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 46px;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 0 14px;
  color: #171717;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
`;

const ReqStar = styled.span`
  color: #e44862;
  margin-left: 2px;
`;

export default function NewCustomerManualPaymentModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const handleClose = () => {
    setFormData(initialFormData);
    setError("");
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!formData.amount.trim()) return "Amount is required";
    if (Number(formData.amount) <= 0) return "Amount must be greater than 0";
    if (!formData.contact.trim()) return "Contact Number is required";
    const cleanPhone = formData.contact.replace(/\D/g, "");
    if (!cleanPhone || cleanPhone.length < 10) return "Please enter a valid contact number (at least 10 digits)";
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setLoading(true);

    try {
      const cleanContact = formData.contact.trim();
      const numAmount = Number(formData.amount);
      const customerFullName = `${formData.firstName} ${formData.lastName}`.trim();

      const res = await createManualCustomerPaymentAPI({
        amount: numAmount,
        payment_method: formData.paymentMethod,
        contact_details: cleanContact,
        first_name: formData.firstName.trim() || undefined,
        last_name: formData.lastName.trim() || undefined,
        customer_name: customerFullName || undefined,
        transaction_reference: formData.transactionReference.trim() || undefined,
        description: formData.description.trim() || undefined,
      });

      if (res != null) {
        showSuccessToast("Manual payment recorded successfully!");
        trackClarityEvent(CLARITY_EVENTS.CUSTOMER_PAYMENT_LINK_GENERATED, {
          amount: numAmount,
          customer_name: customerFullName || cleanContact,
          contact_details: cleanContact,
          payment_method: formData.paymentMethod,
          checkout_mode: "manual",
        });
        onSuccess?.();
        handleClose();
      }
    } catch (err) {
      console.error(err);
      setError("Failed to record manual payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={handleClose}>
        <ModalShell style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={11} />
                  Manual Payment
                </HeroBadge>
                <ModalTitle>New Customer Manual Payment</ModalTitle>
                <ModalSubtitle>
                  Record an offline payment (Cash, UPI, Bank Transfer, etc.) for a new customer.
                </ModalSubtitle>
              </HeroText>
              <CloseBtn type="button" onClick={handleClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            {error && <ErrorSpan style={{ display: "block" }}>{error}</ErrorSpan>}

            <form onSubmit={handleSubmit}>
              <FormCard>
                <FieldGrid>
                  <Field>
                    <Label htmlFor="firstName">
                      <FormLabelIcon icon={User} />
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="lastName">
                      <FormLabelIcon icon={User} />
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </Field>
                </FieldGrid>

                <FieldGrid>
                  <Field>
                    <Label htmlFor="amount">
                      <FormLabelIcon icon={IndianRupee} />
                      Amount (₹) <ReqStar>*</ReqStar>
                    </Label>
                    <Input
                      id="amount"
                      name="amount"
                      type="number"
                      placeholder="e.g. 5000"
                      min={1}
                      value={formData.amount}
                      onChange={handleChange}
                      required
                    />
                  </Field>

                  <Field>
                    <Label htmlFor="paymentMethod">
                      <FormLabelIcon icon={CreditCard} />
                      Payment Method <ReqStar>*</ReqStar>
                    </Label>
                    <Select
                      id="paymentMethod"
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleChange}
                    >
                      {MANUAL_PAYMENT_METHODS.map((method) => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </FieldGrid>

                <Field>
                  <Label htmlFor="contact">
                    <FormLabelIcon icon={Phone} />
                    Contact / Mobile Number <ReqStar>*</ReqStar>
                  </Label>
                  <Input
                    id="contact"
                    name="contact"
                    placeholder="10-digit mobile number"
                    value={formData.contact}
                    onChange={handleChange}
                    required
                  />
                </Field>

                <Field>
                  <Label htmlFor="transactionReference">
                    <FormLabelIcon icon={Hash} />
                    Transaction Reference (Optional)
                  </Label>
                  <Input
                    id="transactionReference"
                    name="transactionReference"
                    placeholder="e.g. UPI Ref / Cheque No / Receipt No"
                    value={formData.transactionReference}
                    onChange={handleChange}
                  />
                </Field>

                <Field>
                  <Label htmlFor="description">
                    <FormLabelIcon icon={FileText} />
                    Description / Notes
                  </Label>
                  <TextArea
                    id="description"
                    name="description"
                    placeholder="e.g. Photography event advance payment"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Field>
              </FormCard>

              <ModalActions style={{ marginTop: 20 }}>
                <SecondaryButton type="button" onClick={handleClose}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={loading || !formData.amount}>
                  {loading ? <Spinner size={16} /> : <Receipt size={16} />}
                  {loading ? "Recording..." : "Record Payment"}
                </PrimaryButton>
              </ModalActions>
            </form>
          </ModalBody>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
}
